import type { Expedition, ItemStack, VehicleDef, VehicleInstance } from './types'
import { GARAGE_LEVEL_CAPACITY, GARAGE_SLOT_COST, getVehicleDef } from './vehicles'

/** Highest garage workstation level we have capacity data for */
const MAX_GARAGE_LEVEL_WITH_DATA = Math.max(
  ...Object.keys(GARAGE_LEVEL_CAPACITY).map(Number),
)

export function vehicleSlotCostForDef(def: VehicleDef | undefined): number {
  if (!def) return 0
  return GARAGE_SLOT_COST[def.size] ?? 0
}

export function vehicleSlotCostForInstance(
  instance: VehicleInstance,
): number {
  return vehicleSlotCostForDef(getVehicleDef(instance.defId))
}

/**
 * Max small-equivalent slots for a built garage at `garageWorkstationLevel`.
 * Level 0 / unbuilt → 0 capacity.
 */
export function maxGarageSlots(garageWorkstationLevel: number): number {
  const lv = Math.floor(garageWorkstationLevel)
  if (lv < 1) return 0
  const capped = Math.min(lv, MAX_GARAGE_LEVEL_WITH_DATA)
  return GARAGE_LEVEL_CAPACITY[capped] ?? GARAGE_LEVEL_CAPACITY[2]
}

export function totalGarageSlotsUsed(vehicles: VehicleInstance[]): number {
  return vehicles.reduce((sum, v) => sum + vehicleSlotCostForInstance(v), 0)
}

export function garageSlotsRemaining(
  garageWorkstationLevel: number,
  vehicles: VehicleInstance[],
): number {
  return Math.max(0, maxGarageSlots(garageWorkstationLevel) - totalGarageSlotsUsed(vehicles))
}

export function garageCanFitVehicle(
  garageWorkstationLevel: number,
  vehicles: VehicleInstance[],
  candidateDefId: string,
): boolean {
  const cap = maxGarageSlots(garageWorkstationLevel)
  if (cap <= 0) return false
  const cost = vehicleSlotCostForDef(getVehicleDef(candidateDefId))
  if (cost <= 0) return false
  return totalGarageSlotsUsed(vehicles) + cost <= cap
}

/** True when save state exceeds capacity (e.g. legacy); scrap/downgrade until within cap */
export function garageIsOverCapacity(
  garageWorkstationLevel: number,
  vehicles: VehicleInstance[],
): boolean {
  const cap = maxGarageSlots(garageWorkstationLevel)
  if (cap <= 0) return vehicles.length > 0
  return totalGarageSlotsUsed(vehicles) > cap
}

export function garageCanFitVehicleInstances(
  garageWorkstationLevel: number,
  instances: VehicleInstance[],
): boolean {
  const cap = maxGarageSlots(garageWorkstationLevel)
  if (cap <= 0) return instances.length === 0
  return totalGarageSlotsUsed(instances) <= cap
}

export function vehicleIsOnActiveExpedition(
  expeditions: Expedition[],
  vehicleInstanceId: string,
): boolean {
  return expeditions.some(
    (exp) =>
      exp.status === 'active' &&
      (exp.vehicleInstanceId === vehicleInstanceId ||
        (exp.vehicleInstanceIds?.includes(vehicleInstanceId) ?? false)),
  )
}

/**
 * Scrap yield when dismantling a vehicle (Phase 2 — consumed by store).
 */
export function scrapYieldForVehicleDef(defId: string): ItemStack[] {
  const def = getVehicleDef(defId)
  if (!def) return [{ defId: 'scrap-rusty', quantity: 2 }]
  switch (def.size) {
    case 'small':
      return [
        { defId: 'scrap-rusty', quantity: 3 },
        { defId: 'scrap-wire', quantity: 2 },
      ]
    case 'medium':
      return [
        { defId: 'scrap-rusty', quantity: 6 },
        { defId: 'scrap-wire', quantity: 3 },
        { defId: 'scrap-electronics', quantity: 1 },
      ]
    case 'large':
      return [
        { defId: 'scrap-rusty', quantity: 10 },
        { defId: 'scrap-wire', quantity: 5 },
        { defId: 'scrap-electronics', quantity: 2 },
        { defId: 'scrap-plank', quantity: 2 },
      ]
    default:
      return [{ defId: 'scrap-rusty', quantity: 2 }]
  }
}
